import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCaisses, useComptesBancaires } from '@/hooks/queries/useBanques';
import {
  useBulkMarquerDettesPayees,
  type DetteFournisseurRow,
} from '@/hooks/queries/useDettesFournisseurs';
import type { DossierPaiementMode } from '@/hooks/queries/usePaiementsFormateurs';
import { MoneyAmount } from '@/components/design-system';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  dettes: DetteFournisseurRow[];
  entrepriseId: string;
  onDone?: () => void;
}

const MODES: { code: DossierPaiementMode; label: string }[] = [
  { code: 'virement', label: 'Virement' },
  { code: 'cheque', label: 'Chèque' },
  { code: 'especes', label: 'Espèces' },
  { code: 'ccp', label: 'CCP' },
  { code: 'carte', label: 'Carte' },
];

export function MarquerDettesPayeesEnLotDialog({
  open,
  onOpenChange,
  dettes,
  entrepriseId,
  onDone,
}: Props): JSX.Element {
  const bulk = useBulkMarquerDettesPayees();
  const { data: caisses = [] } = useCaisses(entrepriseId);
  const { data: comptes = [] } = useComptesBancaires(entrepriseId);

  const [dateReglement, setDateReglement] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState<DossierPaiementMode>('virement');
  const [caisseId, setCaisseId] = useState('');
  const [compteId, setCompteId] = useState('');

  useEffect(() => {
    if (!open) return;
    setDateReglement(new Date().toISOString().slice(0, 10));
    setMode('virement');
    setCaisseId('');
    setCompteId('');
  }, [open]);

  useEffect(() => {
    if (mode === 'especes' && caisses.length > 0 && !caisseId) setCaisseId(caisses[0]?.id ?? '');
    if (mode !== 'especes' && comptes.length > 0 && !compteId) setCompteId(comptes[0]?.id ?? '');
  }, [mode, caisses, comptes, caisseId, compteId]);

  const isEspeces = mode === 'especes';
  const targetMissing = isEspeces ? !caisseId : !compteId;
  const eligibleDettes = dettes.filter((d) => d.statut !== 'paye');
  const totalReste = eligibleDettes.reduce((a, d) => a + (d.montant_dette - d.montant_regle), 0);

  const handleConfirm = async (): Promise<void> => {
    if (eligibleDettes.length === 0) {
      toast.warning('Aucune dette à solder (toutes déjà payées)');
      return;
    }
    if (targetMissing) {
      toast.error(isEspeces ? 'Sélectionnez la caisse' : 'Sélectionnez le compte bancaire');
      return;
    }
    try {
      const result = await bulk.mutateAsync({
        dettes: eligibleDettes,
        dateReglement,
        modePaiement: mode,
        caisseId: isEspeces ? caisseId : null,
        compteBancaireId: isEspeces ? null : compteId,
      });
      if (result.errors.length === 0) {
        toast.success(`${result.success} dette(s) soldée(s) · opérations Caisse créées`);
      } else {
        toast.warning(
          `${result.success} OK · ${result.errors.length} échec(s) — ex: ${result.errors[0]?.message ?? ''}`,
        );
      }
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Solder dettes en lot · {eligibleDettes.length} dette(s)</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3 text-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-xs uppercase tracking-widest text-emerald-700">
                Total à solder
              </span>
              <MoneyAmount amount={totalReste} variant="large" />
            </div>
            {dettes.length - eligibleDettes.length > 0 && (
              <div className="mt-1 text-xs text-slate-600">
                {dettes.length - eligibleDettes.length} dette(s) déjà payée(s) seront ignorées
              </div>
            )}
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 text-xs">
            Chaque dette sera soldée intégralement (reste à payer = montant_dette − montant_regle).
            <strong> Une opération Caisse SORTIE par dette</strong>, mêmes date / mode / cible.
          </div>

          <Field label="Date de règlement">
            <input
              type="date"
              value={dateReglement}
              onChange={(e) => setDateReglement(e.target.value)}
              className="input"
              required
            />
          </Field>

          <Field label="Mode de paiement">
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {MODES.map((m) => (
                <button
                  key={m.code}
                  type="button"
                  onClick={() => setMode(m.code)}
                  className={cn(
                    'rounded-lg border-2 px-2 py-2 text-xs font-semibold transition-all',
                    mode === m.code
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
                  )}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </Field>

          {isEspeces ? (
            <Field
              label="Caisse débitée"
              hint={caisses.length === 0 ? '⚠ Aucune caisse disponible' : undefined}
              warn={caisses.length === 0}
            >
              <select
                value={caisseId}
                onChange={(e) => setCaisseId(e.target.value)}
                className="input"
                required
              >
                <option value="">— Sélectionner —</option>
                {caisses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom}
                    {c.site ? ` · ${c.site}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <Field
              label="Compte bancaire débité"
              hint={comptes.length === 0 ? '⚠ Aucun compte disponible' : undefined}
              warn={comptes.length === 0}
            >
              <select
                value={compteId}
                onChange={(e) => setCompteId(e.target.value)}
                className="input"
                required
              >
                <option value="">— Sélectionner —</option>
                {comptes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.intitule} {c.numero_compte ? `· ${c.numero_compte}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </DialogBody>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={bulk.isPending || targetMissing || eligibleDettes.length === 0}
            className={cn(
              'inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-700',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            {bulk.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Confirmer · {eligibleDettes.length} dette(s)
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  warn,
  children,
}: {
  label: string;
  hint?: string;
  warn?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">{label}</span>
      {children}
      {hint && (
        <span
          className={cn(
            'mt-0.5 block',
            warn ? 'text-xs font-medium text-amber-600' : 'text-[10px] text-slate-400',
          )}
        >
          {hint}
        </span>
      )}
    </label>
  );
}
